import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { DataLabel } from "../components/common";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { GoogleLogo, Globe, ChartBar, PlugsConnected, ArrowsClockwise } from "@phosphor-icons/react";

const INTEGRATIONS = [
  { key: "google_search_console", label: "Google Search Console", icon: GoogleLogo, field: "site_url", ph: "https://clinicavalorian.es" },
  { key: "bing_webmaster", label: "Bing Webmaster Tools", icon: Globe, field: "site_url", ph: "https://clinicavalorian.es" },
  { key: "google_analytics", label: "Google Analytics 4", icon: ChartBar, field: "property_id", ph: "G-XXXXXXXXXX" },
];

export default function Performance() {
  const [perf, setPerf] = useState(null);
  const [strat, setStrat] = useState(null);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/performance"), api.get("/strategy")]);
    setPerf(p.data); setStrat(s.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (key, patch) => {
    const integrations = { ...strat.integrations, [key]: { ...strat.integrations[key], ...patch } };
    const { data } = await api.put("/strategy", { integrations });
    setStrat(data); toast.success("Intégration mise à jour");
  };

  const sync = async (key) => {
    try {
      const { data } = await api.post(`/performance/sync/${key}`);
      if (data.status === "needs_credentials")
        toast.warning("Identifiants requis", { description: `Manquants : ${data.missing_env.join(", ")}` });
      else if (data.status === "error")
        toast.error("Échec de la synchronisation", { description: data.error });
      else {
        toast.success("Synchronisation réussie", { description: `${data.inserted} ligne(s) importée(s)` });
        await load();
      }
    } catch (e) { toast.error("Synchronisation impossible"); }
  };

  if (!perf || !strat) return <div className="font-mono text-xs text-muted-foreground">Chargement…</div>;
  const metrics = perf.metrics || [];

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-1">Boucle de performance</div>
        <h1 className="font-serif text-4xl text-brand tracking-tight">Performance</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {INTEGRATIONS.map((it) => {
          const conf = strat.integrations[it.key] || {};
          return (
            <div key={it.key} data-testid={`integration-${it.key}`} className="bg-white border border-border rounded-md p-5">
              <div className="flex items-center justify-between mb-3">
                <it.icon size={26} className="text-brand" />
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${conf.connected ? "bg-emerald-50 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>
                  {conf.connected ? "Connecté" : "Non connecté"}</span>
              </div>
              <div className="text-sm font-medium">{it.label}</div>
              <input defaultValue={conf[it.field] || ""} placeholder={it.ph}
                onBlur={(e) => save(it.key, { [it.field]: e.target.value })}
                className="w-full mt-3 px-2.5 py-1.5 rounded-md border border-border text-xs font-mono focus:outline-none focus:ring-2 focus:ring-brand/30" />
              <button data-testid={`connect-${it.key}`} onClick={() => save(it.key, { connected: !conf.connected })}
                className={`w-full mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  conf.connected ? "border border-border hover:bg-secondary" : "bg-brand hover:bg-brand-hover text-white"}`}>
                <PlugsConnected size={14} /> {conf.connected ? "Déconnecter" : "Connecter"}
              </button>
              <button data-testid={`sync-${it.key}`} onClick={() => sync(it.key)}
                className="w-full mt-1.5 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs text-muted-foreground hover:text-brand transition-colors">
                <ArrowsClockwise size={13} /> Synchroniser
              </button>
            </div>
          );
        })}
      </div>

      {perf.learning?.recommendations?.length > 0 && (
        <div className="mb-8">
          <DataLabel>Recommandations de stratégie (apprentissage IA)</DataLabel>
          <div className="mt-3 bg-white border border-border rounded-md p-5">
            <ul className="space-y-2">
              {perf.learning.recommendations.map((r, i) => (
                <li key={i} className="text-sm text-foreground flex gap-2"><span className="text-brand">↳</span><span>{r}</span></li>
              ))}
            </ul>
            {perf.learning.applied?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border text-xs text-emerald-700">
                Appliqué automatiquement : {perf.learning.applied.join(" · ")}
              </div>
            )}
          </div>
        </div>
      )}

      <DataLabel>Performance de recherche</DataLabel>
      <div className="mt-3 bg-white border border-border rounded-md p-6">
        {metrics.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              Les connecteurs Search Console, Bing et Analytics sont câblés nativement. Renseignez vos identifiants ci-dessus pour
              activer l'ingestion réelle des impressions, clics et positions — la boucle d'apprentissage ajustera alors la stratégie éditoriale.
            </p>
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground/70 mt-3">Aucune donnée pour le moment</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={metrics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
              <XAxis dataKey="date" stroke="#57606A" fontSize={11} />
              <YAxis stroke="#57606A" fontSize={11} />
              <Tooltip />
              <Line type="monotone" dataKey="clicks" stroke="#0B3B2D" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="impressions" stroke="#2563EB" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
